import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterGroup, TableExplorerColumn } from '../../core/types/workspaceTypes';
import { hostCacheIdentity, type TableExplorerRelationshipPort } from '../components/tableExplorerHostPorts';

/** Rows a host picker searches locally — one page of the target (Azure Table: ≤ 1000 entities). */
export const FK_PICKER_HOST_ROW_LIMIT = 1_000;

type Args = {
  port: TableExplorerRelationshipPort | undefined;
  open: boolean;
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  targetResolved: boolean;
  /** The target-selection / edit flows need the table list. */
  needTables: boolean;
  targetFilter?: FilterGroup[];
};

/**
 * The FK row picker's data through a host relationship port (S4 review C,
 * M7) — tables, target columns and candidate rows — so an Azure Table picker
 * never calls `useAvailableTables`, `useTableMetadata` or
 * `fetchTableExplorerRows`. Every query is disabled without a port (SQL), and
 * every key lives under the port's cache scope (reconnect / mutation aware).
 */
export function useFkPickerHostSource({ port, open, connectionId, database, schema, table, targetResolved, needTables, targetFilter }: Args) {
  const scope = port?.cacheScope ?? ['host-relationship', connectionId];
  const tablesQuery = useQuery({
    queryKey: [...scope, 'fk-picker-tables', database],
    queryFn: () => port!.listTables({ connectionId, database }),
    enabled: !!port && open && needTables,
    staleTime: 5 * 60 * 1000,
  });
  const columnsQuery = useQuery({
    queryKey: [...scope, 'fk-picker-columns', database, schema, table],
    queryFn: () => port!.listColumns({ connectionId, database, schema, table }),
    enabled: !!port && open && targetResolved && !!table,
    staleTime: 5 * 60 * 1000,
  });
  const rowsQuery = useQuery({
    queryKey: [...scope, 'fk-picker-rows', database, schema, table, hostCacheIdentity(targetFilter ?? [])],
    queryFn: () => port!.searchRows!({ connectionId, database, schema, table, targetFilter, limit: FK_PICKER_HOST_ROW_LIMIT }),
    enabled: !!port?.searchRows && open && targetResolved && !!table,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const columns = useMemo<TableExplorerColumn[]>(() => (columnsQuery.data ?? []).map((column) => ({
    name: column.name, dataType: column.dataType, nullable: true,
    isPrimaryKey: column.isPrimaryKey === true, isForeignKey: false,
  })), [columnsQuery.data]);
  const primaryKeyColumns = useMemo(() => new Set(columns.filter((column) => column.isPrimaryKey).map((column) => column.name)), [columns]);

  return {
    tables: tablesQuery.data,
    columns,
    primaryKeyColumns,
    columnsLoading: columnsQuery.isLoading,
    columnsError: columnsQuery.error as Error | null,
    rows: rowsQuery.data?.rows,
    rowsLoading: rowsQuery.isLoading,
    rowsError: rowsQuery.error as Error | null,
  };
}

/** Local search + sort over the host page (the SQL picker does both server-side). */
export function searchHostPickerRows(input: {
  rows: readonly Record<string, unknown>[];
  search: string;
  searchColumns: readonly (string | null | undefined)[];
  sort: { field: string; direction: 'asc' | 'desc' } | null;
  limit: number;
}): Record<string, unknown>[] {
  const query = input.search.trim().toLowerCase();
  const columns = input.searchColumns.filter((column): column is string => !!column);
  let rows = query
    ? input.rows.filter((row) => columns.some((column) => String(row[column] ?? '').toLowerCase().includes(query)))
    : [...input.rows];
  if (input.sort) {
    const { field, direction } = input.sort;
    const factor = direction === 'asc' ? 1 : -1;
    rows = rows.sort((left, right) => {
      const a = left[field]; const b = right[field];
      if (a === b) return 0;
      if (a === undefined || a === null) return 1;
      if (b === undefined || b === null) return -1;
      return (a < b ? -1 : 1) * factor;
    });
  }
  return rows.slice(0, input.limit);
}
